import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { message } from 'antd';
import { ERoutesPrivate } from 'common/models/routesModel';
import { AppDispatch, history } from 'app/store';
import {
  EAnnouncementSuccessMessage,
  IAnnouncementCollection,
  IAnnouncementCollectionFilter,
  IAnnouncementCreateData,
  IAnnouncementModel,
  IAnnouncementUpdateData,
} from 'entities/Announcement/Announcement.models';
import { announcementTransport } from 'entities/Announcement/Announcement.trasport';

export interface IState {
  // announcement collection
  announcementCollectionLoading: boolean;
  announcementCollection: IAnnouncementCollection | null;
  announcementCollectionError: IError | null;
  announcementCollectionParams: IAnnouncementCollectionFilter | null;
  // announcement model
  announcementModelLoading: boolean;
  announcementModel: IAnnouncementModel | null;
  announcementModelError: IError | null;
  announcementModelParams: IAnnouncementCreateData | IAnnouncementUpdateData | null;
}

const initialState: IState = {
  // announcement collection
  announcementCollectionLoading: false,
  announcementCollection: null,
  announcementCollectionError: null,
  announcementCollectionParams: null,
  // announcement model
  announcementModelLoading: false,
  announcementModel: null,
  announcementModelError: null,
  announcementModelParams: null,
};

export const announcementSlice = createSlice({
  name: 'announcement',
  initialState,
  reducers: {
    // announcement collection
    setAnnouncementCollectionLoading(state, action: PayloadAction<boolean>) {
      state.announcementCollectionLoading = action.payload;
    },
    setAnnouncementCollection(state, action: PayloadAction<IAnnouncementCollection | null>) {
      state.announcementCollection = action.payload;
    },
    setAnnouncementCollectionError(state, action: PayloadAction<IError | null>) {
      state.announcementCollectionError = action.payload;
    },
    setAnnouncementCollectionParams(state, action: PayloadAction<IAnnouncementCollectionFilter | null>) {
      state.announcementCollectionParams = action.payload;
    },
    // announcement model
    setAnnouncementModelLoading(state, action: PayloadAction<boolean>) {
      state.announcementModelLoading = action.payload;
    },
    setAnnouncementModel(state, action: PayloadAction<IAnnouncementModel | null>) {
      state.announcementModel = action.payload;
    },
    setAnnouncementModelError(state, action: PayloadAction<IError | null>) {
      state.announcementModelError = action.payload;
    },
    setAnnouncementModelParams(state, action: PayloadAction<IAnnouncementCreateData | IAnnouncementUpdateData | null>) {
      state.announcementModelParams = action.payload;
    },
  },
});

export const {
  // announcement collection
  setAnnouncementCollectionLoading,
  setAnnouncementCollection,
  setAnnouncementCollectionError,
  setAnnouncementCollectionParams,
  // announcement model
  setAnnouncementModelLoading,
  setAnnouncementModel,
  setAnnouncementModelError,
  setAnnouncementModelParams,
} = announcementSlice.actions;
export default announcementSlice.reducer;

export const getAnnouncementCollection = (filter?: IAnnouncementCollectionFilter) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setAnnouncementCollectionLoading(true));

    try {
      const announcementCollection = await announcementTransport.getCollection(filter);

      dispatch(setAnnouncementCollection(announcementCollection));
    } catch (error) {
      const _error = error as IError;

      dispatch(setAnnouncementCollectionError(_error));
    } finally {
      dispatch(setAnnouncementCollectionLoading(false));
    }
  }

  return thunk;
};

export const getAnnouncementModel = (id: string) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setAnnouncementModelLoading(true));

    try {
      const announcementModel = await announcementTransport.get(id);

      dispatch(setAnnouncementModel(announcementModel));
    } catch (error) {
      const _error = error as IError;

      dispatch(setAnnouncementModelError(_error));
    } finally {
      dispatch(setAnnouncementModelLoading(false));
    }
  }

  return thunk;
};

export const addAnnouncementModel = (params: IAnnouncementCreateData) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setAnnouncementModelLoading(true));
    dispatch(setAnnouncementModelParams(params));

    try {
      await announcementTransport.add(params);

      message.success(EAnnouncementSuccessMessage.Create);
      history.push(ERoutesPrivate.Announcements);

      dispatch(setAnnouncementModel(null));
      dispatch(setAnnouncementModelParams(null));
    } catch (error) {
      const _error = error as IError;

      dispatch(setAnnouncementModelError(_error));
    } finally {
      dispatch(setAnnouncementModelLoading(false));
    }
  }

  return thunk;
};

// Unpublish / publish back. Stays on the page: after unpublish the model returns as
// pending, which unlocks the edit form right away.
export const updateAnnouncementVisibility = (id: string, isHidden: boolean) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setAnnouncementModelLoading(true));

    try {
      const model = await announcementTransport.updateVisibility(id, isHidden);

      dispatch(setAnnouncementModel(model));
      message.success(isHidden ? EAnnouncementSuccessMessage.Hidden : EAnnouncementSuccessMessage.Published);
    } catch (error) {
      dispatch(setAnnouncementModelError(error as IError));
    } finally {
      dispatch(setAnnouncementModelLoading(false));
    }
  }

  return thunk;
};

export const updateAnnouncementModel = (params: IAnnouncementUpdateData) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setAnnouncementModelLoading(true));
    dispatch(setAnnouncementModelParams(params));

    try {
      await announcementTransport.update(params);

      message.success(EAnnouncementSuccessMessage.Edit);
      history.push(ERoutesPrivate.Announcements);

      dispatch(setAnnouncementModel(null));
      dispatch(setAnnouncementModelParams(null));
    } catch (error) {
      const _error = error as IError;

      dispatch(setAnnouncementModelError(_error));
    } finally {
      dispatch(setAnnouncementModelLoading(false));
    }
  }

  return thunk;
};
